import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getParentAvatar } from '../constants/avatars';
import { subscribeToMessages, addParentUpdate, deleteThread } from '../services/chatService';
import './ParentView.css';

function ParentView() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [editingQaIndex, setEditingQaIndex] = useState(null);
  const [updateText, setUpdateText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    // Subscribe to real-time message updates from Firestore
    const unsubscribe = subscribeToMessages(currentUser.uid, (firestoreMessages) => {
      setMessages(firestoreMessages);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid]);

  // Filter to only show Q&A pairs (user questions with bot answers)
  const qaPairs = [];
  for (let i = 0; i < messages.length; i++) {
    const userMsg = messages[i];
    if (!userMsg.isBot && userMsg.text) {
      // Find the next bot message that matches this question
      // First try to find by question property, then by position
      const botMsg = messages.find((msg, idx) => 
        idx > i && 
        msg.isBot && 
        !msg.isParentUpdate && // Exclude parent updates from being the main answer
        (msg.question === userMsg.text || (!msg.question && idx === i + 1))
      );
      if (botMsg && botMsg.text) {
        // Find all parent updates for this question
        const parentUpdates = messages.filter(msg => 
          msg.isParentUpdate && msg.replyToQuestionId === userMsg.id
        ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        qaPairs.push({
          question: userMsg.text,
          answer: botMsg.text,
          illustration: botMsg.illustration,
          timestamp: userMsg.timestamp || botMsg.timestamp,
          questionId: userMsg.id,
          answerId: botMsg.id,
          parentUpdates: parentUpdates
        });
      }
    }
  }

  const handleUpdateClick = (index) => {
    setEditingQaIndex(index);
    setUpdateText('');
  };

  const handleCancelUpdate = () => {
    setEditingQaIndex(null);
    setUpdateText('');
  };

  const handleSubmitUpdate = async (qaPair) => {
    if (!updateText.trim() || isSubmitting || !currentUser?.uid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await addParentUpdate(
        currentUser.uid,
        updateText.trim(),
        qaPair.questionId,
        qaPair.question
      );
      setEditingQaIndex(null);
      setUpdateText('');
    } catch (error) {
      console.error('Error adding parent update:', error);
      alert('Failed to add update. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteThread = async (qaPair) => {
    if (!currentUser?.uid) {
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to permanently delete this entire thread?\n\n' +
      `Question: "${qaPair.question}"\n\n` +
      'This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingThreadId(qaPair.questionId);
    try {
      const parentUpdateIds = qaPair.parentUpdates.map(update => update.id).filter(Boolean);
      await deleteThread(
        currentUser.uid,
        qaPair.questionId,
        qaPair.answerId,
        parentUpdateIds
      );
    } catch (error) {
      console.error('Error deleting thread:', error);
      alert('Failed to delete thread. Please try again.');
      setDeletingThreadId(null);
    }
  };


  return (
    <div className="parent-view">
      <div className="parent-header">
        <div className="parent-header-content">
          <div className="parent-avatar-container">
            <img 
              src={getParentAvatar(currentUser)} 
              alt={currentUser?.displayName || 'Parent'} 
              className="parent-avatar"
            />
          </div>
          <div className="parent-header-text">
            <h1>Parent Review</h1>
            <p className="subtitle">Review your child's questions and answers</p>
          </div>
        </div>
      </div>
      
      <div className="qa-list">
        {qaPairs.length === 0 ? (
          <div className="empty-state">
            <p>No questions have been asked yet.</p>
            <p>Questions and answers will appear here once your child starts chatting.</p>
          </div>
        ) : (
          qaPairs.map((qa, index) => (
            <div key={index} className="qa-card">
              <div className="qa-header">
                <span className="qa-number">Q&A #{index + 1}</span>
                {qa.timestamp && (
                  <span className="qa-date">
                    {new Date(qa.timestamp).toLocaleString()}
                  </span>
                )}
              </div>
              
              <div className="qa-question">
                <h3>Question:</h3>
                <p>{qa.question}</p>
              </div>
              
              <div className="qa-answer">
                <h3>Answer:</h3>
                <div className="answer-content">
                  {qa.answer.split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>
              
              {qa.illustration && (
                <div className="qa-illustration">
                  <h3>Illustration:</h3>
                  <img 
                    src={qa.illustration} 
                    alt="Illustration" 
                    className="illustration-preview"
                  />
                </div>
              )}

              {qa.parentUpdates && qa.parentUpdates.length > 0 && (
                <div className="qa-parent-updates">
                  <h3>Parent Updates:</h3>
                  {qa.parentUpdates.map((update, updateIndex) => (
                    <div key={update.id || updateIndex} className="parent-update-item">
                      <div className="parent-update-content">
                        <strong>Parent Update: </strong>{update.text}
                      </div>
                      {update.timestamp && (
                        <div className="parent-update-timestamp">
                          {new Date(update.timestamp).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="qa-actions">
                <button 
                  className="update-answer-button"
                  onClick={() => handleUpdateClick(index)}
                >
                  ✏️ Update Answer
                </button>
                <button 
                  className="delete-thread-button"
                  onClick={() => handleDeleteThread(qa)}
                  disabled={deletingThreadId === qa.questionId}
                  title="Delete thread"
                >
                  {deletingThreadId === qa.questionId ? '...' : '🗑️'}
                </button>
              </div>

              {editingQaIndex === index && (
                <div className="update-modal">
                  <div className="update-modal-content">
                    <h3>Add More Information</h3>
                    <p className="update-modal-question">Replying to: "{qa.question}"</p>
                    <textarea
                      className="update-textarea"
                      value={updateText}
                      onChange={(e) => setUpdateText(e.target.value)}
                      placeholder="Add more information or clarification to the answer..."
                      rows={4}
                      disabled={isSubmitting}
                    />
                    <div className="update-modal-actions">
                      <button
                        className="update-cancel-button"
                        onClick={handleCancelUpdate}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        className="update-submit-button"
                        onClick={() => handleSubmitUpdate(qa)}
                        disabled={!updateText.trim() || isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Update'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ParentView;

